import { useState } from "react";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { Check, Briefcase } from "lucide-react";
import { BrandMark } from "@/brand/BrandMark";
import { BrandHero } from "@/brand/BrandHero";
import { toast } from "sonner";

function onlyDigits(s) { return String(s || "").replace(/\D/g, ""); }
function validateCNPJ(cnpj) {
  cnpj = onlyDigits(cnpj);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const w1 = [5,4,3,2,9,8,7,6,5,4,3,2];
  const w2 = [6,...w1];
  const dv = (base, weights) => {
    const s = weights.reduce((acc,w,i) => acc + Number(base[i]) * w, 0);
    const r = s % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = dv(cnpj.slice(0,12), w1);
  const d2 = dv(cnpj.slice(0,12) + d1, w2);
  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
}
function maskCNPJ(v) {
  return onlyDigits(v).slice(0,14)
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

export default function PublicCheckinEmpresa() {
  const [form, setForm] = useState({
    cnpj: "", razao_social: "", inscricao_estadual: "", email: "", telefone: "", endereco: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!validateCNPJ(form.cnpj)) return toast.error("CNPJ inválido. Confira os dígitos.");
    if (!form.razao_social.trim()) return toast.error("Informe a razão social");
    if (!form.email.trim() || !form.telefone.trim() || !form.endereco.trim())
      return toast.error("Email, telefone e endereço são obrigatórios");
    setLoading(true);
    try {
      await publicApi.post("/public/checkin/empresa", { ...form, cnpj: onlyDigits(form.cnpj) });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <Toaster richColors position="top-right" />
      <header className="relative h-64 md:h-80 overflow-hidden">
        <BrandHero testid="checkin-empresa-brand-hero" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-transparent" />
        <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-end">
          <div className="flex items-center gap-3 text-white mb-4">
            <div className="w-10 h-10 rounded-md bg-[#FFFFFF]/90 flex items-center justify-center shadow-sm">
              
              <BrandMark variant="mark" size={28} />
            </div>
            <div className="font-brand-title text-xl text-white font-bold leading-none">Rede Hospeda</div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E9C46A] mb-2 font-semibold">Check-in online — Pessoa Jurídica</div>
          <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight max-w-2xl">
            Bem-vindo à Rede Hospeda
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {submitted ? (
          <div data-testid="checkin-empresa-success" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-6 text-[#083A6B]">
              <Check size={28} strokeWidth={1.8} />
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">Cadastro empresarial recebido</div>
            <h2 className="font-serif text-3xl text-stone-900 mb-3">Obrigado!</h2>
            <p className="text-stone-600 max-w-md mx-auto leading-relaxed">
              Recebemos os dados de {form.razao_social}. Nossa equipe entrará em contato para confirmar as reservas contratadas.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-8 md:p-10 space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-2 font-semibold flex items-center gap-2">
                <Briefcase size={12} /> Dados da empresa
              </div>
              <h2 className="font-serif text-3xl text-stone-900">Check-in empresarial</h2>
              <p className="text-stone-600 mt-2 text-sm">
                Cadastro rápido para empresas que contratam hospedagem. Preencha os dados fiscais.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>CNPJ *</Label>
                <Input data-testid="input-empresa-cnpj" value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: maskCNPJ(e.target.value) })}
                  placeholder="00.000.000/0000-00" maxLength={18} />
              </div>
              <div>
                <Label>Razão social *</Label>
                <Input data-testid="input-empresa-razao" value={form.razao_social}
                  onChange={(e) => setForm({ ...form, razao_social: e.target.value })} />
              </div>
              <div>
                <Label>Inscrição estadual (opcional)</Label>
                <Input data-testid="input-empresa-ie" value={form.inscricao_estadual}
                  onChange={(e) => setForm({ ...form, inscricao_estadual: e.target.value })} />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" data-testid="input-empresa-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Telefone *</Label>
                <Input data-testid="input-empresa-telefone" value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
              </div>
              <div>
                <Label>Endereço *</Label>
                <Input data-testid="input-empresa-endereco" value={form.endereco}
                  onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
              </div>
            </div>
            <Button type="submit" data-testid="btn-empresa-submit" disabled={loading}
              className="w-full h-12 bg-[#083A6B] hover:bg-[#052B50] text-white rounded-md">
              {loading ? "Enviando…" : "Enviar cadastro"}
            </Button>
            <div className="text-center">
              <a href="/checkin" data-testid="link-pf" className="text-xs text-stone-500 hover:text-[#083A6B] underline">
                Pessoa física? Volte ao check-in individual
              </a>
            </div>
          </form>
        )}

        <footer className="text-center text-xs text-stone-500 mt-12">
          Rede Hospeda · Gestão de Pousadas · WhatsApp (11) 91417-5095
        </footer>
      </main>
    </div>
  );
}
